import { createContext, useContext, useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  signUp?: (email: string, password: string) => Promise<{ error: Error | null }>;
};

const AuthContext = createContext<AuthContextType | null>(null);

const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL || 'admin@iwd-japanwomen.org';
const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || '';

// Supabase未設定時用の簡易認証（VITE_ADMIN_PASSWORDが設定されていれば使用）
function useSimpleAuth() {
  const [authenticated, setAuthenticated] = useState(() =>
    typeof window !== 'undefined' ? sessionStorage.getItem('admin_auth') === '1' : false
  );

  const signIn = async (email: string, password: string) => {
    if (ADMIN_PASSWORD && password === ADMIN_PASSWORD && (email === ADMIN_EMAIL || email === 'admin')) {
      sessionStorage.setItem('admin_auth', '1');
      setAuthenticated(true);
      return { error: null };
    }
    return { error: new Error('メールアドレスまたはパスワードが正しくありません') };
  };

  const signOut = async () => {
    sessionStorage.removeItem('admin_auth');
    setAuthenticated(false);
  };

  return { user: authenticated ? ({ email: ADMIN_EMAIL } as User) : null, signIn, signOut };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const simple = useSimpleAuth();

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!isSupabaseConfigured() || !supabase) {
      return simple.signIn(email, password);
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? new Error(error.message) : null };
  };

  const signOut = async () => {
    if (isSupabaseConfigured() && supabase) {
      await supabase.auth.signOut();
      setUser(null);
    } else {
      await simple.signOut();
    }
  };

  const isAuthed = isSupabaseConfigured() ? !!user : !!simple.user;
  const value: AuthContextType = {
    user: isAuthed ? (user || ({ email: ADMIN_EMAIL } as User)) : null,
    loading: isSupabaseConfigured() ? loading : false,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
